import React, { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  es: {
    // Common
    'app.name': 'Gestor de Respuestas Pro',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.create': 'Crear',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.loading': 'Cargando...',
    'common.noResults': 'Sin resultados',
    'common.today': 'Hoy',
    'common.close': 'Cerrar',
    'common.add': 'Agregar',
    'common.name': 'Nombre',
    'common.description': 'Descripcion',
    'common.status': 'Estado',
    'common.priority': 'Prioridad',
    'common.dueDate': 'Fecha de vencimiento',
    'common.assignees': 'Asignados',
    'common.comments': 'Comentarios',
    'common.settings': 'Ajustes',

    // Navigation
    'nav.home': 'Inicio',
    'nav.inbox': 'Bandeja de entrada',
    'nav.myTasks': 'Mis tareas',
    'nav.goals': 'Metas',
    'nav.spaces': 'Espacios',

    // Task statuses
    'status.planning': 'Planificacion',
    'status.inProgress': 'En curso',
    'status.completed': 'Finalizado',

    // Priorities
    'priority.high': 'Alta',
    'priority.medium': 'Media',
    'priority.low': 'Baja',

    // Views
    'view.list': 'Lista',
    'view.board': 'Tablero',
    'view.gantt': 'Gantt',
    'view.calendar': 'Calendario',
    'view.dashboard': 'Panel',
    'view.workload': 'Carga',

    // Task drawer
    'task.subtasks': 'Subtareas',
    'task.dependencies': 'Dependencias',
    'task.customFields': 'Campos personalizados',
    'task.activity': 'Actividad',
    'task.timeTracking': 'Registro de tiempo',
    'task.approval': 'Aprobacion',
    'task.milestone': 'Hito',
    'task.recurring': 'Recurrente',
    'task.addComment': 'Escribe un comentario...',

    // Goals
    'goals.title': 'Metas y Portfolios',
    'goals.newGoal': 'Nueva Meta',
    'goals.keyResults': 'Resultados Clave',
    'goals.linkedProjects': 'Vincular Proyectos',

    // Settings
    'settings.general': 'General',
    'settings.notifications': 'Notificaciones',
    'settings.integrations': 'Integraciones',
    'settings.language': 'Idioma',

    // Offline
    'offline.message': 'Sin conexion - Modo offline. Los cambios se sincronizaran al reconectar.',
  },

  en: {
    // Common
    'app.name': 'Response Manager Pro',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.loading': 'Loading...',
    'common.noResults': 'No results',
    'common.today': 'Today',
    'common.close': 'Close',
    'common.add': 'Add',
    'common.name': 'Name',
    'common.description': 'Description',
    'common.status': 'Status',
    'common.priority': 'Priority',
    'common.dueDate': 'Due date',
    'common.assignees': 'Assignees',
    'common.comments': 'Comments',
    'common.settings': 'Settings',

    // Navigation
    'nav.home': 'Home',
    'nav.inbox': 'Inbox',
    'nav.myTasks': 'My Tasks',
    'nav.goals': 'Goals',
    'nav.spaces': 'Spaces',

    // Task statuses
    'status.planning': 'Planning',
    'status.inProgress': 'In Progress',
    'status.completed': 'Completed',

    // Priorities
    'priority.high': 'High',
    'priority.medium': 'Medium',
    'priority.low': 'Low',

    // Views
    'view.list': 'List',
    'view.board': 'Board',
    'view.gantt': 'Gantt',
    'view.calendar': 'Calendar',
    'view.dashboard': 'Dashboard',
    'view.workload': 'Workload',

    // Task drawer
    'task.subtasks': 'Subtasks',
    'task.dependencies': 'Dependencies',
    'task.customFields': 'Custom Fields',
    'task.activity': 'Activity',
    'task.timeTracking': 'Time Tracking',
    'task.approval': 'Approval',
    'task.milestone': 'Milestone',
    'task.recurring': 'Recurring',
    'task.addComment': 'Write a comment...',

    // Goals
    'goals.title': 'Goals & Portfolios',
    'goals.newGoal': 'New Goal',
    'goals.keyResults': 'Key Results',
    'goals.linkedProjects': 'Link Projects',

    // Settings
    'settings.general': 'General',
    'settings.notifications': 'Notifications',
    'settings.integrations': 'Integrations',
    'settings.language': 'Language',

    // Offline
    'offline.message': 'No connection - Offline mode. Changes will sync when reconnected.',
  },
};

const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [locale, setLocale] = useState(() => {
    return localStorage.getItem('app-locale') || 'es';
  });

  const t = useCallback((key) => {
    return translations[locale]?.[key] || translations['es']?.[key] || key;
  }, [locale]);

  const changeLocale = useCallback((newLocale) => {
    if (translations[newLocale]) {
      setLocale(newLocale);
      localStorage.setItem('app-locale', newLocale);
    }
  }, []);

  const availableLocales = [
    { code: 'es', label: 'Espanol' },
    { code: 'en', label: 'English' },
  ];

  return (
    <I18nContext.Provider value={{ locale, t, changeLocale, availableLocales }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    return { locale: 'es', t: (key) => key, changeLocale: () => {}, availableLocales: [] };
  }
  return context;
};
